import { memo } from "react";
import { useTranslation } from "react-i18next";
import { User } from "../../types/Types";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { isPaidPortalMember } from "../../utils/membershipTier";
import { formatBusinessTurnoverGbp } from "../../utils/businessTurnover";
import {
  AccountBodyText,
  AccountContactRow,
  AccountProfileCard,
  AccountTagField,
  AccountTagGrid,
} from "./AccountProfileLayout";

interface BusinessProfileProps {
  userData: User;
}

function useBusinessProfileCopy(userData: User) {
  const { t, i18n } = useTranslation();
  const np = () => t("account.notProvided");
  const dynamicFields = {
    businessName: userData.businessName,
    businessSectorIndustry: userData.businessSectorIndustry,
    businessCountryOperation: userData.businessCountryOperation,
    businessAbout: userData.businessAbout,
    personalAbout: userData.personalAbout,
    businessWebsiteUrl: userData.businessWebsiteUrl,
    businessObjectives: userData.businessObjectives,
    email: userData.email,
    businessTelephoneNumber: userData.businessTelephoneNumber,
    personalLanguagePreference: userData.personalLanguagePreference,
    personalGender: userData.personalGender,
    businessCurrency: userData.businessCurrency,
    businessTurnover: userData.businessTurnover,
    county: userData.county,
    businessPostCode: userData.businessPostCode,
    businessAddressLineOne: userData.businessAddressLineOne,
    businessAddressLineTwo: userData.businessAddressLineTwo,
    businessProductServices: userData.businessProductServices,
    productInterested: userData.productInterested,
    businessDiscountCode: userData.businessDiscountCode,
    businessDiscount: userData.businessDiscount,
    membershipName: userData.membershipName,
    referralCode: userData.referralCode,
    referralCodeUsed: userData.referralCodeUsed,
  };
  const { data: translated } = useTranslateDynamicFields(
    dynamicFields,
    i18n.resolvedLanguage || i18n.language,
  );
  const tx = (key: keyof typeof dynamicFields, fallback?: string | null) =>
    translated?.[key] || fallback || np();

  return { t, np, tx, showMembershipFields: isPaidPortalMember(userData) };
}

const BusinessProfile = memo(function BusinessProfile({
  userData,
}: BusinessProfileProps) {
  const { t, np, tx } = useBusinessProfileCopy(userData);
  const aboutBusinessText = tx("businessAbout", userData.businessAbout);
  const aboutYouText = tx("personalAbout", userData.personalAbout);
  const websiteText = userData.businessWebsiteUrl
    ? tx("businessWebsiteUrl", userData.businessWebsiteUrl)
    : np();

  return (
    <div dir="ltr">
      <AccountProfileCard title={t("account.field.aboutYou")}>
        <AccountBodyText
          textForMeasure={aboutYouText}
          label={t("account.field.aboutYou")}
        >
          {aboutYouText}
        </AccountBodyText>
      </AccountProfileCard>

      <AccountProfileCard title={t("account.section.businessDetails")}>
        <AccountTagGrid>
          <AccountTagField
            label={t("account.field.businessName")}
            value={tx("businessName", userData.businessName)}
            textValue={tx("businessName", userData.businessName)}
          />
          <AccountTagField
            label={t("account.field.sectorIndustry")}
            value={tx("businessSectorIndustry", userData.businessSectorIndustry)}
            textValue={tx(
              "businessSectorIndustry",
              userData.businessSectorIndustry,
            )}
          />
          <AccountTagField
            label={t("account.field.countriesOfOperation")}
            value={tx(
              "businessCountryOperation",
              userData.businessCountryOperation,
            )}
            textValue={tx(
              "businessCountryOperation",
              userData.businessCountryOperation,
            )}
          />
          <AccountTagField
            label={t("account.field.websiteUrl")}
            textValue={websiteText}
            value={
              userData.businessWebsiteUrl ? (
                <a
                  href={userData.businessWebsiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  {websiteText}
                </a>
              ) : (
                np()
              )
            }
          />
          <AccountTagField
            label={t("account.field.objectives")}
            value={tx("businessObjectives", userData.businessObjectives)}
            textValue={tx("businessObjectives", userData.businessObjectives)}
          />
        </AccountTagGrid>
      </AccountProfileCard>

      <AccountProfileCard title={t("account.field.aboutBusiness")}>
        <AccountBodyText
          textForMeasure={aboutBusinessText}
          label={t("account.field.aboutBusiness")}
        >
          {aboutBusinessText}
        </AccountBodyText>
      </AccountProfileCard>

      <AccountProfileCard title={t("account.section.offerings")}>
        <AccountTagGrid>
          <AccountTagField
            label={t("account.field.productServices")}
            value={tx(
              "businessProductServices",
              userData.businessProductServices,
            )}
            textValue={tx(
              "businessProductServices",
              userData.businessProductServices,
            )}
          />
          <AccountTagField
            label={t("account.field.productInterested")}
            value={tx("productInterested", userData.productInterested)}
            textValue={tx("productInterested", userData.productInterested)}
          />
          <AccountTagField
            label={t("account.field.discountCode")}
            value={tx("businessDiscountCode", userData.businessDiscountCode)}
            textValue={tx(
              "businessDiscountCode",
              userData.businessDiscountCode,
            )}
          />
          <AccountTagField
            label={t("account.field.discount")}
            value={tx("businessDiscount", userData.businessDiscount)}
            textValue={tx("businessDiscount", userData.businessDiscount)}
          />
        </AccountTagGrid>
      </AccountProfileCard>
    </div>
  );
});

BusinessProfile.displayName = "BusinessProfile";

export function BusinessMembershipTags({ userData }: BusinessProfileProps) {
  const { t, tx, showMembershipFields } = useBusinessProfileCopy(userData);

  return (
    <AccountTagGrid>
      {showMembershipFields ? (
        <>
          <AccountTagField
            label={t("account.field.membershipName")}
            value={tx("membershipName", userData.membershipName)}
            textValue={tx("membershipName", userData.membershipName)}
          />
          <AccountTagField
            label={t("account.field.membershipType")}
            value={
              userData.isAnnualMembership
                ? t("account.billingCycle.annual")
                : t("account.billingCycle.monthly")
            }
            textValue={
              userData.isAnnualMembership
                ? t("account.billingCycle.annual")
                : t("account.billingCycle.monthly")
            }
          />
        </>
      ) : null}
      <AccountTagField
        label={t("account.field.referralCode")}
        value={tx("referralCode", userData.referralCode)}
        textValue={tx("referralCode", userData.referralCode)}
      />
      {userData.referralCodeUsed ? (
        <AccountTagField
          label={t("account.field.referralCodeUsed")}
          value={tx("referralCodeUsed", userData.referralCodeUsed)}
          textValue={tx("referralCodeUsed", userData.referralCodeUsed)}
        />
      ) : null}
    </AccountTagGrid>
  );
}

export function BusinessContactDetails({ userData }: BusinessProfileProps) {
  const { t, np, tx } = useBusinessProfileCopy(userData);
  const turnover =
    formatBusinessTurnoverGbp(userData.businessTurnover, t) || np();

  const row = (label: string, value: string) => (
    <AccountContactRow label={label} value={value} title={value} />
  );

  return (
    <>
      {row(t("account.field.email"), tx("email", userData.email))}
      {row(
        t("account.field.contactNumber"),
        tx("businessTelephoneNumber", userData.businessTelephoneNumber),
      )}
      {row(
        t("account.field.languagePreference"),
        tx(
          "personalLanguagePreference",
          userData.personalLanguagePreference,
        ),
      )}
      {row(t("account.field.gender"), tx("personalGender", userData.personalGender))}
      {row(
        t("account.field.currency"),
        tx("businessCurrency", userData.businessCurrency),
      )}
      {row(`${t("account.field.turnover")} (GBP)`, turnover)}
      {row(t("account.field.county"), tx("county", userData.county))}
      {row(
        t("account.field.postalCode"),
        tx("businessPostCode", userData.businessPostCode),
      )}
      {row(
        t("account.field.addressLineOne"),
        tx("businessAddressLineOne", userData.businessAddressLineOne),
      )}
      {row(
        t("account.field.addressLineTwo"),
        tx("businessAddressLineTwo", userData.businessAddressLineTwo),
      )}
    </>
  );
}

export default BusinessProfile;
